import { queryOptions } from "@tanstack/react-query";
import { listAiaMembers } from "./aia.functions";
import type { AiaMember } from "./aia.queries";

/**
 * Advisory-board fragment cache, read side. The people-api Lambda rewrites
 * two prebuilt HTML snippets in the web bucket on every roster change
 * (infra/lambda/_shared/board-snippet.ts); fetching them is a plain static
 * read through CloudFront — no API call, no database, no cold Aurora resume.
 *
 * A missing key does NOT 404 here: the SPA fallback answers with index.html,
 * and local dev has no snippet at all. So a payload only counts if it carries
 * the generator's marker attribute; anything else throws and the caller falls
 * back to the live listAiaMembers query.
 */

async function fetchSnippet(path: string, marker: string): Promise<string> {
  const res = await fetch(path, { headers: { accept: "text/html" } });
  if (!res.ok) throw new Error(`Snippet fetch failed (${res.status})`);
  const html = await res.text();
  if (!html.includes(marker)) throw new Error("Snippet missing or not yet generated");
  return html;
}

export const boardSnippetQueryOptions = () =>
  queryOptions({
    queryKey: ["board_snippet_website"],
    queryFn: () =>
      fetchSnippet("/snippets/advisory-board.html", 'data-mii-snippet="advisory-board"'),
    staleTime: 5 * 60_000,
    retry: 1,
  });

/** The email-safe variant, for the admin newsletter builder. */
export function fetchEmailBoardSnippet(): Promise<string> {
  return fetchSnippet(
    "/snippets/advisory-board-email.html",
    'data-mii-snippet="advisory-board-email"',
  );
}

/**
 * The full roster as DATA (all columns, bios included) for the /board page —
 * same rows listAiaMembers returns, snapshotted to snippets/advisory-board.json
 * on every roster change. The fallback lives inside the queryFn so the route
 * loader and useSuspenseQuery need no special handling: snapshot first, live
 * query only when the snapshot is missing or malformed. The admin roster
 * screen deliberately stays on the live query (it must see saves instantly).
 */
export const boardRosterQueryOptions = () =>
  queryOptions({
    queryKey: ["aia_advisory_members_snapshot"],
    queryFn: async (): Promise<AiaMember[]> => {
      try {
        const res = await fetch("/snippets/advisory-board.json", {
          headers: { accept: "application/json" },
        });
        if (!res.ok) throw new Error(`Roster snapshot fetch failed (${res.status})`);
        const data = (await res.json()) as { snippet?: string; members?: AiaMember[] };
        if (data?.snippet !== "advisory-board-roster" || !Array.isArray(data.members)) {
          throw new Error("Roster snapshot missing or not yet generated");
        }
        return data.members;
      } catch {
        return (await listAiaMembers()) as AiaMember[];
      }
    },
    staleTime: 5 * 60_000,
  });
